import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import React, { useState } from "react";

import { CustomField } from "0.Mine/3.Interfaces/Client";

interface CustomFieldsManagerProps {
  isOpen: boolean;
  onClose: () => void;
  customFields: CustomField[];
  onAdd: (field: Omit<CustomField, "id">) => Promise<CustomField>;
  onUpdate: (id: string, field: Partial<CustomField>) => Promise<CustomField>;
  onDelete: (id: string) => Promise<void>;
}

export function CustomFieldsManager({
  isOpen,
  onClose,
  customFields,
  onAdd,
  onUpdate,
  onDelete,
}: CustomFieldsManagerProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [newField, setNewField] = useState<Omit<CustomField, "id">>({
    name: "",
    type: "text",
  });
  const [options, setOptions] = useState<string>("");

  const resetForm = () => {
    setEditingField(null);
    setNewField({ name: "", type: "text" });
    setOptions("");
  };

  const handleAddField = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const fieldToAdd: Omit<CustomField, "id"> = {
        ...newField,
        options:
          newField.type === "select"
            ? options
                .split(",")
                .map((o) => o.trim())
                .filter(Boolean)
            : undefined,
      };
      await onAdd(fieldToAdd);
      resetForm();
    } catch (err) {
      console.error("Errore aggiunta campo:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateField = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingField) return;
    setIsLoading(true);
    try {
      const updates: Partial<CustomField> = {
        name: newField.name,
        type: newField.type,
      };
      if (newField.type === "select") {
        updates.options = options
          .split(",")
          .map((o) => o.trim())
          .filter(Boolean);
      }
      await onUpdate(editingField.id, updates);
      resetForm();
    } catch (err) {
      console.error("Errore aggiornamento campo:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (
      window.confirm(
        "Sei sicuro di voler eliminare questo campo personalizzato? Tutti i dati correlati saranno rimossi."
      )
    ) {
      setIsLoading(true);
      try {
        await onDelete(id);
      } catch (err) {
        console.error("Errore eliminazione campo:", err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const startEdit = (field: CustomField) => {
    setEditingField(field);
    setNewField({ name: field.name, type: field.type });
    setOptions(field.options?.join(", ") ?? "");
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Gestisci Campi Personalizzati</DialogTitle>
      <DialogContent dividers>
        <Box
          component="form"
          onSubmit={editingField ? handleUpdateField : handleAddField}
          sx={{ mb: 4 }}
        >
          <Typography variant="h6" gutterBottom>
            {editingField ? "Modifica Campo Personalizzato" : "Aggiungi Nuovo Campo Personalizzato"}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <TextField
              label="Nome campo"
              name="name"
              value={newField.name}
              onChange={(e) => setNewField((prev) => ({ ...prev, name: e.target.value }))}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="type-label">Tipo campo</InputLabel>
              <Select
                labelId="type-label"
                label="Tipo campo"
                value={newField.type}
                onChange={(e) =>
                  setNewField((prev) => ({ ...prev, type: e.target.value as CustomField["type"] }))
                }
                fullWidth
              >
                <MenuItem value="text">Testo</MenuItem>
                <MenuItem value="number">Numero</MenuItem>
                <MenuItem value="date">Data</MenuItem>
                <MenuItem value="select">Selezione (dropdown)</MenuItem>
              </Select>
            </FormControl>
            {newField.type === "select" && (
              <TextField
                label="Opzioni (separate da virgola)"
                value={options}
                onChange={(e) => setOptions(e.target.value)}
                placeholder="Opzione1, Opzione2, Opzione3"
                required
                fullWidth
              />
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
            {editingField && (
              <Button onClick={resetForm} disabled={isLoading}>
                Annulla modifica
              </Button>
            )}
            <Button type="submit" variant="contained" disabled={isLoading} startIcon={<AddIcon />}>
              {isLoading ? (
                <CircularProgress size={20} />
              ) : editingField ? (
                "Aggiorna Campo"
              ) : (
                "Aggiungi Campo"
              )}
            </Button>
          </Box>
        </Box>

        <Typography variant="h6" gutterBottom>
          Campi Personalizzati Esistenti
        </Typography>
        {customFields.length === 0 ? (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Nessun campo personalizzato definito.
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gap: 2 }}>
            {customFields.map((field) => (
              <Paper
                key={field.id}
                elevation={1}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="subtitle1">{field.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tipo: {field.type}
                    {field.type === "select" && field.options && (
                      <> • Opzioni: {field.options.join(", ")}</>
                    )}
                  </Typography>
                </Box>
                <Box>
                  <IconButton onClick={() => startEdit(field)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(field.id)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
}
